/**
 * TrendCardGrid — 3-column grid of CovCardOverlay cinematic cards
 *
 * DESIGN:
 *   • 3-col desktop → 2-col tablet → 1-col mobile
 *   • Each card is CovCardOverlay variant="cinematic"
 *   • Scroll-reveal: staggered fade + translateY via IntersectionObserver
 *   • hoverContent shows description + color swatch+name chips (mirrors imagio PortfolioCard)
 */

import { CovCardOverlay } from "@cx-component-library/react-components";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { TrendCardItem, TrendColor } from "../trendData";
import "./TrendCardGrid.css";

interface TrendCardGridProps {
  cards: TrendCardItem[];
  trendSlug: string;
  /** When true, card clicks do not navigate to the card detail page (used when no cardDetails are defined for this trend entry). */
  disableNavigation?: boolean;
  /** When true (default), the last row expands to fill the grid width. Set to false to keep uniform card sizes. */
  expandLastRow?: boolean;
}

/** Swatch (12×12 square) + color name label — mirrors imagio OverlayColorChip */
const ColorChip = ({ hex, name }: TrendColor) => {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const nearWhite =
    parseInt(full.slice(0, 2), 16) >= 225 &&
    parseInt(full.slice(2, 4), 16) >= 225 &&
    parseInt(full.slice(4, 6), 16) >= 225;

  return (
    <div className="trend-card-grid__color-chip">
      <span
        className="trend-card-grid__color-swatch"
        style={{
          backgroundColor: hex,
          border: nearWhite
            ? "1px solid rgba(255,255,255,0.42)"
            : "1px solid rgba(255,255,255,0.18)",
        }}
      />
      <span className="trend-card-grid__color-name">{name}</span>
    </div>
  );
};

export const TrendCardGrid = ({
  cards,
  trendSlug,
  disableNavigation,
  expandLastRow = true,
}: TrendCardGridProps) => {
  const navigate = useNavigate();
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const cardEls = Array.from(
      grid.querySelectorAll<HTMLElement>(".trend-card-grid__item"),
    );

    // Reveal all cards when the grid enters view; stagger handled by CSS transition-delay
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          for (const el of cardEls)
            el.classList.add("trend-card-grid__item--visible");
          observer.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -32px 0px" },
    );

    observer.observe(grid);
    return () => observer.disconnect();
  }, [cards]);

  if (cards.length === 0) {
    return (
      <div className="trend-card-grid trend-card-grid--empty">
        <p className="trend-card-grid__empty-text">
          Trends coming soon — check back later.
        </p>
      </div>
    );
  }

  const total = cards.length;
  const remainder = total > 2 ? total % 3 : 0;
  const lastRowStart = remainder > 0 ? total - remainder : -1;
  const useSubgrid = total > 2;
  const gridCols = useSubgrid ? "repeat(6, 1fr)" : `repeat(${total}, 1fr)`;

  return (
    <div
      className="trend-card-grid"
      ref={gridRef}
      style={{ gridTemplateColumns: gridCols }}
    >
      {cards.map((card, i) => {
        const hoverContent = (
          <div className="cov-card-overlay__cin-items">
            <div className="cov-card-overlay__cin-divider" aria-hidden="true" />
            {card.description && (
              <p className="trend-card-grid__description">{card.description}</p>
            )}
            {card.colors && card.colors.length > 0 && (
              <div className="trend-card-grid__color-chips">
                {card.colors.map((color) => (
                  <ColorChip key={color.hex} {...color} />
                ))}
              </div>
            )}
          </div>
        );

        const isLastRow = lastRowStart >= 0 && i >= lastRowStart;
        const span = !useSubgrid
          ? undefined
          : isLastRow && expandLastRow && remainder === 2
            ? 3
            : isLastRow && expandLastRow && remainder === 1
              ? 6
              : 2;

        return (
          <div
            key={card.slug}
            className="trend-card-grid__item"
            style={{
              transitionDelay: `${i * 75}ms`,
              gridColumn: span ? `span ${span}` : undefined,
            }}
          >
            <CovCardOverlay
              variant="cinematic"
              image={card.image}
              header={card.title}
              hoverContent={hoverContent}
              onCardClick={
                disableNavigation
                  ? undefined
                  : () => navigate(`/trend/${trendSlug}/${card.slug}`)
              }
              style={{ height: "360px", width: "100%" }}
            />
          </div>
        );
      })}
    </div>
  );
};
